import { F0Dialog, F0Form, useF0Form } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'
import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { useCreateCompensationPlanForm } from './form/useCreateCompensationPlanForm'
import {
  RECURRENCE_TO_DATE_FIELD,
  type BasicsStepData,
} from 'modules/incentives/components/CreateCompensationPlanDialog/steps/basics/schema'
import {
  IncentivesCreateCompensationPlanDocument,
  IncentivesCreateCompensationPlanAssignableAdditionalCompensationsDocument,
  IncentivesCompensationPlansDataCollectionDocument,
} from 'generated/resources/graphql'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { i18nDateToString } from 'modules/core/lib/dates'
import app from 'modules/core/store/models/app'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

interface CreateCompensationPlanDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (planId: string) => void
}

export const CreateCompensationPlanDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: CreateCompensationPlanDialogProps) => {
  const { company } = useCurrentSession()
  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const [fetchAssignableCompensations, { error: assignableCompensationsError }] = useLazyQuery(
    IncentivesCreateCompensationPlanAssignableAdditionalCompensationsDocument
  )

  const [createCompensationPlanMutation] = useMutation(IncentivesCreateCompensationPlanDocument, {
    extractorFn: (result) => result.incentivesMutations.createIncentivesCompensationPlan,
    refetchQueries: [IncentivesCompensationPlansDataCollectionDocument],
  })

  const handleSubmit = async (data: BasicsStepData): Promise<boolean> => {
    const fieldName = RECURRENCE_TO_DATE_FIELD[data.recurrence]
    const date = data[fieldName]

    if (!date) return false

    const assignableResult = await fetchAssignableCompensations({
      variables: {
        recurrence: data.recurrence,
        payrollConceptIds: data.payrollConceptId ? [data.payrollConceptId] : undefined,
        legalEntityIds:
          data.filterScope === 'legal_entity' && data.legalEntityIds?.length
            ? data.legalEntityIds
            : undefined,
        teamIds: data.filterScope === 'team' && data.teamIds?.length ? data.teamIds : undefined,
      },
    })

    if (assignableCompensationsError) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('incentives.wizard.create.error_additional_compensations'),
      })
      return false
    }

    const contractsAdditionalCompensationIds =
      assignableResult.data?.incentives.assignableAdditionalCompensations.map((c) => c.id) ?? []

    if (contractsAdditionalCompensationIds.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('incentives.wizard.create.no_additional_compensation_message'),
      })
      return false
    }

    const result = await createCompensationPlanMutation({
      variables: {
        companyId: company.id,
        name: data.name,
        recurrence: data.recurrence,
        startsOn: i18nDateToString(date, 'backend'),
        contractsAdditionalCompensationIds,
      },
    })

    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('incentives.wizard.create.error_message'),
      })
    )

    if (!handled.success) return false
    const planId = handled?.data?.compensationPlan?.id

    if (!planId) return false

    app.setFlash({
      type: 'correct',
      message: i18n.t('incentives.wizard.create.success_message'),
    })
    onSuccess?.(planId)
    onClose()
    return true
  }

  const { formDefinition } = useCreateCompensationPlanForm({
    onSubmit: handleSubmit,
  })

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('incentives.wizard.title')}
      primaryAction={{
        label: i18n.t('common.create'),
        onClick: submit,
        loading: isSubmitting,
        disabled: hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}
